import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { materialService } from '../services/supabase';
import { FiFilm, FiEdit2, FiTrash2, FiPlus, FiCheck, FiX, FiSearch, FiBook, FiVideo } from 'react-icons/fi';
import { normalizeImageUrl } from '../utils/imageUtils';

// Importando as imagens padrão
import defaultVideoThumb from '../assets/images/thumbnails/thumb-video.webp';
import defaultBookThumb from '../assets/images/thumbnails/thumb-livro.webp';
import defaultMagazineThumb from '../assets/images/thumbnails/thumb-revista.webp';

type Material = {
  id: string;
  titulo: string;
  descricao: string;
  tipo: 'vídeo' | 'livro' | 'revista';
  iframe: string;
  thumb_url: string | null;
};

const MaterialList: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const { data, error } = await materialService.getMaterials();
      
      if (error) {
        throw new Error(error.message);
      }
      
      setMaterials(data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar materiais');
      console.error('Erro ao buscar materiais:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (material: Material) => {
    setMaterialToDelete(material);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!materialToDelete) return;
    
    try {
      setLoading(true);
      const { error } = await materialService.deleteMaterial(materialToDelete.id);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Atualiza a lista de materiais após a exclusão
      setMaterials(materials.filter(material => material.id !== materialToDelete.id));
      setShowDeleteModal(false);
      setMaterialToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir material');
      console.error('Erro ao excluir material:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setMaterialToDelete(null);
  };

  // Filtra materiais por termo de busca
  const filteredMaterials = materials.filter(material => 
    material.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || 
    material.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Função para renderizar ícone baseado no tipo de material
  const renderMaterialIcon = (tipo: string) => {
    switch (tipo) {
      case 'vídeo':
        return <FiVideo className="text-blue-400" />;
      case 'livro':
        return <FiBook className="text-green-400" />;
      case 'revista':
        return <FiFilm className="text-purple-400" />;
      default:
        return <FiFilm />;
    }
  };

  // Obter a imagem padrão apropriada baseada no tipo de material
  const getDefaultThumbnail = (tipo: string) => {
    switch (tipo) {
      case 'vídeo':
        return defaultVideoThumb;
      case 'livro':
        return defaultBookThumb;
      case 'revista':
        return defaultMagazineThumb;
      default:
        return defaultVideoThumb;
    }
  };

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway mb-2">
          Gerenciamento de <span className="text-vermelho-claro">Materiais</span>
        </h1>
        <p className="text-gray-300">
          Visualize, adicione, edite e exclua materiais educacionais da plataforma.
        </p>
      </div>

      {/* Barra de ações */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-auto md:min-w-[300px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Buscar materiais..."
            className="input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <Link 
          to="/materiais/novo" 
          className="btn btn-primary py-2 px-4 flex items-center w-full md:w-auto justify-center"
        >
          <FiPlus className="mr-2" />
          Novo Material
        </Link>
      </div>

      {/* Lista de materiais */}
      {loading && materials.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-center">
          <p className="text-red-200">{error}</p>
          <button 
            onClick={fetchMaterials}
            className="mt-2 text-vermelho-claro hover:underline"
          >
            Tentar novamente
          </button>
        </div>
      ) : filteredMaterials.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-lg">
          <div className="w-16 h-16 bg-gray-800 rounded-full mx-auto flex items-center justify-center mb-4">
            <FiFilm className="text-gray-600" size={24} />
          </div>
          <h3 className="text-xl font-medium text-gray-400 mb-2">
            Nenhum material encontrado
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {materials.length === 0 
              ? 'Não há materiais cadastrados. Clique em "Novo Material" para adicionar.'
              : 'Nenhum material corresponde aos termos de busca.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMaterials.map(material => (
            <div 
              key={material.id} 
              className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 transition-all hover:shadow-lg hover:shadow-vermelho-escuro/20 hover:-translate-y-1"
            >
              <div className="aspect-w-16 aspect-h-9 relative">
                {material.thumb_url ? (
                  <img 
                    src={normalizeImageUrl(material.thumb_url) || getDefaultThumbnail(material.tipo)} 
                    alt={material.titulo} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.log('Erro ao carregar imagem:', material.thumb_url);
                      (e.target as HTMLImageElement).src = getDefaultThumbnail(material.tipo);
                    }}
                  />
                ) : (
                  <img 
                    src={getDefaultThumbnail(material.tipo)} 
                    alt={material.titulo} 
                    className="w-full h-full object-cover"
                  />
                )}
                
              </div>
              
              <div className="p-4">
                <h3 className="text-md font-semibold text-white mb-1 truncate">{material.titulo}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 h-10 mb-3">
                  {material.descricao}
                </p>
                
                <div className="flex justify-between mt-2">
                  <Link
                    to={`/materiais/${material.id}`}
                    className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center"
                  >
                    <FiEdit2 className="mr-1" size={14} /> Editar
                  </Link>
                  <button
                    onClick={() => handleDeleteClick(material)}
                    className="text-red-400 hover:text-red-300 text-sm flex items-center"
                  >
                    <FiTrash2 className="mr-1" size={14} /> Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && materialToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={handleDeleteCancel}></div>
          <div className="relative bg-gray-900 rounded-lg max-w-md w-full mx-auto z-10 overflow-hidden animate-modal-appear">
            <div className="p-6">
              <div className="mb-4 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-red-900/50 flex items-center justify-center mb-4">
                  <FiTrash2 className="text-red-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Excluir Material</h3>
                <p className="text-gray-300">
                  Tem certeza que deseja excluir o material <span className="font-bold text-white">{materialToDelete.titulo}</span>?
                </p>
                <p className="mt-2 text-sm text-red-400">
                  Esta ação não pode ser desfeita e removerá todos os dados associados.
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 py-2 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                >
                  <FiX className="mr-2" /> Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                >
                  <FiCheck className="mr-2" /> Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default MaterialList; 